import React, {
  useMemo, useState, useEffect, useCallback, useRef,
} from 'react';
import find from 'lodash/find';
import mapValues from 'lodash/fp/mapValues';
import isEmpty from 'lodash/isEmpty';
import Form, { FormConsumer } from '@active/react-ui/Form';
import { withI18n } from '@active/react-utils/i18n/LocaleContext';
import Locale from '@active/react-utils/i18n/Locale';
import { useSelector } from 'react-redux';
import encrypt from './utils/encrypt';
import SavedAccount, * as types from './components';
import MandateFields, { MandateCheckbox } from './components/MandateFields';
import { dateTime } from '../../../../../DateTime';
import { connectCommonProps } from '../../../../../../../shared/utils/CommonPropsContext';

import {
  useCreateECheckAccountMutation,
} from '../../../../../../../store/services/common/payment/paymentApi';
import pubSubInstance from '../../../../../../../shared/utils/pubSub';

const ECheck = ({ i18n }) => {
  const paymentForm = useRef();

  const [ createECheckAccount ] = useCreateECheckAccountMutation();

  const {
    entities: {
      paymentMethods: {
        echeckInfo: {
          modulus,
          exponent,
          type, savedAccounts = [],
          showMandateForm,
          orderId, signatureDate = {},
          currencyCode,
          allowSavingAccount,
          forceSaveInWallet,
        } = {},
      },
    },
    billingContact = {},
  } = useSelector(state => state.paymentMethods);

  const {
    widgetNameSpace,
    locale,
    labelForEcheckNewAccount,
  } = useSelector(state => state.propsData);

  const [ isShowAddECheck, setIsShowAddEcheck ] = useState(savedAccounts.length <= 0);
  const [ accountTypeState, setAccountTypeState ] = useState(savedAccounts.length > 0 ? savedAccounts[0].accountType : 'CONSUMER_SAVINGS');

  const fields = useMemo(() => ({
    selectedAccount: savedAccounts.length > 0 ? savedAccounts[0].id : 'addECheck',
    savingAccount: allowSavingAccount && forceSaveInWallet ? [ 'true' ] : [],
  }), [ savedAccounts, allowSavingAccount, forceSaveInWallet ]);

  const ECheckField = useMemo(() => types[type], [ type ]);

  const dateOfSignature = useMemo(() => dateTime({ date: signatureDate, locale }), [ signatureDate, locale ]);

  const handleValidate = useCallback(() => {
    return new Promise((resolve) => {
      paymentForm.current ? paymentForm.current.validateForm({ shouldTouchFields: true }).then(
        (res) => {
          resolve(isEmpty(res));
        },
      ) : resolve(true);
    });
  }, [ paymentForm.current ]);

  const handleSubmit = () => {
    const {
      bankRoutingNumber = '',
      accountNumber = '',
      accountName,
      accountType = '',

      bicCode = '',
      iban = '',
      savingAccount,
      selectedAccount,

      mandateConsent,
    } = paymentForm.current.values;

    const {
      address: {
        postalCode, stateProvince,
      } = {},
    } = billingContact;
    return new Promise((resolve) => {
      // mandateConsent is required for new  && saved account
      if (showMandateForm && mandateConsent.length === 0) {
        resolve({ status: false, paymentMethodType: 'ECHECK' });
        return;
      }

      // save accout
      if (!!selectedAccount && selectedAccount !== 'addECheck') {
        resolve({ paymentAccountId: selectedAccount, status: true, paymentMethodType: 'ECHECK' });
        return;
      }

      // create accout
      const saveForFutureUse = savingAccount && savingAccount[0] === 'true';
      const bankCountryCode = iban && iban.substring(0, 2);
      const cipherText = encrypt({
        modulus,
        exponent,
        bankRoutingNumber,
        accountNumber,
        accountName: accountName.trim(),
        accountType,
        postalCode,
        stateProvince,
        type,
        bankCountryCode,
        bicCode,
        iban,
      });

      const params = {
        cipherText,
        modulus,
        billingContact,
      };

      createECheckAccount({ params }).unwrap().then((
        { paymentAccount: { id: paymentAccountId } = {} },
      ) => {
        resolve({
          status: true,
          paymentAccountId,
          saveForFutureUse,
          paymentMethodType: 'ECHECK',
        });
      }).catch(() => {
        resolve({
          status: false,
          errorCode: 'ERROR_SAVE_ECHECK_FAILED',
          paymentMethodType: 'ECHECK',
        });
      });
    });
  };

  const toggleCreateAccount = useCallback(({ target: { value: selectedAccount } }) => {
    const { defaultValues } = types[type];
    setIsShowAddEcheck(selectedAccount === 'addECheck');
    paymentForm.current.setValues({
      ...defaultValues,
      mandateConsent: [],
      ...fields,
      selectedAccount,
    });
    paymentForm.current.setTouched(mapValues(() => false)(paymentForm.current.values));
  }, [ type, paymentForm.current, fields ]);

  const handleFormChange = useCallback(({ values: { accountType, selectedAccount } }) => {
    const currentSavedAccount = find(savedAccounts, { id: selectedAccount }) || {};
    setAccountTypeState(isShowAddECheck ? accountType : currentSavedAccount.accountType);
  }, [ savedAccounts, isShowAddECheck ]);

  useEffect(() => {
    pubSubInstance.subscribe(`${widgetNameSpace}-payment-item-validate`, handleValidate);
    pubSubInstance.subscribe(`${widgetNameSpace}-payment-item-submit`, handleSubmit);
    return () => {
      pubSubInstance.unsubscribe(`${widgetNameSpace}-payment-item-validate`, handleValidate);
      pubSubInstance.unsubscribe(`${widgetNameSpace}-payment-item-submit`, handleSubmit);
    };
  }, [ widgetNameSpace, handleValidate, handleSubmit ]);

  return (!!ECheckField && <div className="echeck cui-mg-t-20">
    <Form
      initialValues={{
        ...ECheckField.defaultValues,
        mandateConsent: [],
        ...fields,
      }}
      customizeLayout
      enableReinitialize
      onChange={handleFormChange}>
      {
        type === 'EUDD'
        && <div className={`echeck__support-tip${savedAccounts.length === 0 ? ' tip-mb' : ''}`}>
          <Locale id="checkout.lib.components.payment.echeck.eudd.country.support.tip" />
        </div>
      }
      {
        showMandateForm
        && <MandateFields type={type} orderId={orderId} dateOfSignature={dateOfSignature} />
      }
      <SavedAccount
        savedAccounts={savedAccounts}
        toggleCreateAccount={toggleCreateAccount}
        labelForEcheckNewAccount={labelForEcheckNewAccount}/>
      {
        isShowAddECheck
        && <div className="add-echeck-container">
          <ECheckField i18n={i18n} allowSavingAccount={allowSavingAccount} forceSaveInWallet={forceSaveInWallet}/>
        </div>
      }
      {
        showMandateForm
        && <MandateCheckbox
          type={type}
          currencyCode={currencyCode}
          locale={locale}
          dateOfSignature={dateOfSignature}
          accountType={accountTypeState} />
      }
      <FormConsumer>
        {
          ({
            form,
          }) => {
            paymentForm.current = form;
          }
        }
      </FormConsumer>
    </Form>
  </div>);
};

export default withI18n(connectCommonProps(ECheck));
